/* eslint-disable camelcase */

import { SHOPPER_CONFIG_BASE_PATH } from 'components/shopper/constants';
import {
  BRAND_CENTER_PATH,
  BRAND_OFFERS_PATH,
  BRAND_SOURCES_PATH,
  BRAND_STRATEGY_PATH,
  CONTENT_LIBRARY_PATH,
} from './constants';
import { PRODUCT_CATALOG_PATH } from './product/constants';

export const BrandingLinkCategory = {
  FAQ: 'faq',
  BRAND_GUIDANCE: 'brand_guidance',
  POLICIES: 'policies',
  PRODUCT_GUIDANCE: 'product_guidance',
  SUPPORT: 'support',
  OTHER: 'other',
} as const;

export type BrandingLinkCategory =
  typeof BrandingLinkCategory[keyof typeof BrandingLinkCategory];

export type RangeAttributeBoundary = [number, number];

export interface BrandingStatus {
  ready: boolean;
}

export interface BrandingRangeAttribute {
  inputName: string;
  range: RangeAttributeBoundary;
  value: number;
}

export interface BrandingTextAttribute {
  inputName: string;
  value: string;
}

export interface BrandingMultiTextAttribute {
  inputName: string;
  value: string[];
}

export type BrandingValue =
  | BrandingRangeAttribute['value']
  | BrandingTextAttribute['value']
  | BrandingMultiTextAttribute['value'];

export type BrandingValues = Record<string, BrandingValue>;

export interface BrandingAttribute {
  inputName: string;
  range?: [number, number];
  value: BrandingValue;
}

export type BrandingType = 'marketing' | 'ai_generated' | 'file_ai_generated';

export interface Branding {
  shopId: number;
  guid: string;
  attributes: BrandingAttribute[];
  type: BrandingType;
}

export interface BrandingPayload {
  rangeAttributes: BrandingRangeAttribute[];
  textAttributes: BrandingTextAttribute[];
  multiTextAttributes: BrandingMultiTextAttribute[];
}

export type RangeAttributeBoundaries = Map<string, RangeAttributeBoundary>;

export interface BrandingDefaultValue {
  [key: string]: BrandingValue;
}

export type BrandingDefaultValues = BrandingDefaultValue[];

export interface GeneratedMessage {
  generatedMessage: string;
  originalMessage: string;
  explanation: string;
  trigger: string;
  allowedTags: string[];
  playbooks: Record<string, string>;
}

export interface LogFormChangesProps {
  initialValues: BrandingValues;
  updatedValues: BrandingValues;
  shopId: number;
}

type BrandingSourceOrigin = 'SCRAPED' | 'USER_PROVIDED' | 'ADMIN_PROVIDED';

export interface BrandingFileSource {
  id: number;
  shopId: number;
  name: string;
  objectUrl: string;
  objectKey: string;
  bucketName: string;
  category: BrandingLinkCategory;
  origin: BrandingSourceOrigin;
  active: boolean;
  isContentEditable: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface BrandingFileDownloadUrl {
  downloadUrl: string;
  expiresIn: number;
}

export enum BrandingLinkSourceError {
  INVALID_URL = 'invalid_url',
  INVALID_MODE = 'invalid_mode',
  BLOCKED_URL = 'blocked_url',
  FETCH_FAILED = 'fetch_failed',
  SSL_ERROR = 'ssl_error',
  DNS_RESOLUTION_ERROR = 'dns_resolution_error',
  TIMEOUT = 'timeout',
  RATE_LIMITED = 'rate_limited',
  HTTP_ERROR = 'http_error',
  PARSE_ERROR = 'parse_error',
  EXTRACTION_ERROR = 'extraction_error',
  FORBIDDEN = 'forbidden',
  NOT_FOUND = 'not_found',
  UNAUTHORIZED = 'unauthorized',
  SERVER_ERROR = 'server_error',
}

export interface BrandingLinkSource {
  id: number;
  shopId: number;
  origin: BrandingSourceOrigin;
  errorCode: BrandingLinkSourceError | null;
  nonceId: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  url: string;
  category: BrandingLinkCategory;
  active: boolean;
}

export interface AddLinkSourceParams {
  url: string;
  category: BrandingLinkCategory;
  active: boolean;
  nonceId: string | null;
}

export interface UpdateLinkSourceParams extends AddLinkSourceParams {
  id: number;
}

export interface UpdateFileSourceParams {
  id: number;
  name: string;
  category: BrandingLinkCategory;
  active: boolean;
}

export interface PersistRangeBoundaryParams {
  name: string;
  min: number;
  max: number;
}

type Category = 'automations' | 'campaigns';

export interface InspirationMessages {
  [key: string]: string[];
}

export interface InspirationExamples {
  category: Category;
  event_type: string;
  selection_types: InspirationMessages[];
}

export interface InspirationPayload {
  inspiration_message: string;
  preview_type: string;
}

export const BrandCenterRoutes = {
  VOICE: BRAND_CENTER_PATH,
  STRATEGY: BRAND_STRATEGY_PATH,
  SOURCES: BRAND_SOURCES_PATH,
  CONTENT_LIBRARY: CONTENT_LIBRARY_PATH,
  SHOPPER: SHOPPER_CONFIG_BASE_PATH,
  PRODUCTS: PRODUCT_CATALOG_PATH,
  OFFERS: BRAND_OFFERS_PATH,
} as const;

export interface BrandVoice {
  id: number;
  guidelines: string;
  domain_override?: string;
  created_at: string;
  updated_at: string;
}

export type ProductCatalogMapGenerationStatus =
  | 'idle'
  | 'started'
  | 'in_progress'
  | 'completed'
  | 'no_catalog'
  | 'failed'
  | 'unavailable';

export interface ProductCatalogMapGenerationRequest {
  generationGuidance?: string;
}

export interface ProductCatalogMapGenerationResponse {
  workflowId: string;
  runId: string | null;
  status: ProductCatalogMapGenerationStatus;
  generationGuidance?: string | null;
  productCatalogMap: string | null;
  generatedAt: string | null;
}
